/**
 * Pharmacy Edit Modal
 * 
 * Comprehensive modal for editing pharmacy information with tabbed interface
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import useDraftStore from '../stores/draftStore'
import { useAuthStore } from '../stores/authStore'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  Button,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Textarea,
  Badge
} from './ui'

const PHARMACY_GATEWAYS = ['5Axis', 'SelfShip', 'PharmacyBroker']
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
]

export default function PharmacyEditModal({ pharmacy, isOpen, onClose, onSave, isDraftEdit = false }) {
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const [formData, setFormData] = useState(pharmacy || {})
  const [validationErrors, setValidationErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  
  const { currentClinicId } = useAuthStore()
  const clinicId = currentClinicId || searchParams.get('clinic') || 'tmates'

  // Initialize draft store when modal opens
  useEffect(() => {
    if (isOpen && user && clinicId) {
      const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = useDraftStore.getState()
      
      // Get user ID (backend uses 'id', not '_id')
      const userId = user.id
      
      // Re-initialize if store is not initialized OR if it was initialized with wrong userId/clinicId
      if (!isInitialized || storeUserId !== userId || storeClinicId !== clinicId) {
        console.log('[PHARMACY MODAL] Initializing draft store with:', { clinicId, userId })
        initialize(clinicId, userId)
      }
    }
  }, [isOpen, user, clinicId])

  useEffect(() => {
    if (pharmacy) {
      setFormData({
        pharmacy_name: pharmacy.pharmacy_name || '',
        pharmacy_ncpdpid: pharmacy.pharmacy_ncpdpid || '',
        dosespot_pharmacy_id: pharmacy.dosespot_pharmacy_id || '',
        pharmacy_gateway: pharmacy.pharmacy_gateway || '5Axis',
        phone_number: pharmacy.phone_number || '',
        contact_emails: pharmacy.contact_emails || [''],
        address: {
          address: pharmacy.address?.address || '',
          city: pharmacy.address?.city || '',
          state: pharmacy.address?.state || '',
          zipcode: pharmacy.address?.zipcode || '',
          country: pharmacy.address?.country || 'USA'
        },
        states: pharmacy.states || [],
        states_controlled: pharmacy.states_controlled || [],
        states_non_sterile: pharmacy.states_non_sterile || [],
        states_sterile: pharmacy.states_sterile || [],
        pharmacy_config: {
          enable_pharmacy: pharmacy.pharmacy_config?.enable_pharmacy ?? true,
          weekly_order_capacity: pharmacy.pharmacy_config?.weekly_order_capacity || 100,
          current_week_orders: pharmacy.pharmacy_config?.current_week_orders || 0,
          auto_accept_orders: pharmacy.pharmacy_config?.auto_accept_orders ?? false,
          priority_processing: pharmacy.pharmacy_config?.priority_processing ?? false
        }
      })
      setHasChanges(false)
    }
  }, [pharmacy])

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev }
      const keys = path.split('.')
      let current = newData
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {}
        current = current[keys[i]]
      }
      
      current[keys[keys.length - 1]] = value
      return newData
    })
    setHasChanges(true)
  }

  const addContactEmail = () => {
    updateFormData('contact_emails', [...formData.contact_emails, ''])
  }

  const removeContactEmail = (index) => {
    const newEmails = formData.contact_emails.filter((_, i) => i !== index)
    updateFormData('contact_emails', newEmails.length > 0 ? newEmails : [''])
  }

  const updateContactEmail = (index, value) => {
    const newEmails = [...formData.contact_emails]
    newEmails[index] = value
    updateFormData('contact_emails', newEmails)
  }

  // Generic state management for any state array field
  const addStateToField = (field, state) => {
    const currentStates = formData[field] || []
    if (!currentStates.includes(state)) {
      updateFormData(field, [...currentStates, state])
    }
  }

  const removeStateFromField = (field, state) => {
    const currentStates = formData[field] || []
    updateFormData(field, currentStates.filter(s => s !== state))
  }

  // Legacy functions for backwards compatibility
  const addState = (state) => addStateToField('states', state)
  const removeState = (state) => removeStateFromField('states', state)

  const validateForm = () => {
    const errors = {}
    
    if (!formData.pharmacy_name?.trim()) {
      errors.pharmacy_name = 'Pharmacy name is required'
    }
    
    if (!formData.pharmacy_ncpdpid) {
      errors.pharmacy_ncpdpid = 'NCPDP ID is required'
    }
    
    if (!formData.phone_number?.trim()) {
      errors.phone_number = 'Phone number is required'
    }
    
    if (!formData.contact_emails?.some(email => email.trim())) {
      errors.contact_emails = 'At least one contact email is required'
    }
    
    if (!formData.address?.address?.trim()) {
      errors['address.address'] = 'Address is required'
    }
    
    if (!formData.address?.city?.trim()) {
      errors['address.city'] = 'City is required'
    }
    
    if (!formData.address?.state?.trim()) {
      errors['address.state'] = 'State is required'
    }
    
    if (!formData.address?.zipcode?.trim()) {
      errors['address.zipcode'] = 'Zipcode is required'
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return
    
    setIsLoading(true)
    try {
      if (isDraftEdit) {
        // Just pass updated data back — caller handles updating the draft
        onSave?.(formData)
        onClose()
        setHasChanges(false)
      } else {
        const { addChange } = useDraftStore.getState()

        // Determine if this is a create or update
        const isNewPharmacy = !pharmacy || !pharmacy._id
        const operation = isNewPharmacy ? 'create' : 'update'

        // Build detailed description
        const details = []
        if (formData.pharmacy_ncpdpid) details.push(`NCPDP: ${formData.pharmacy_ncpdpid}`)
        if (formData.pharmacy_gateway) details.push(`Gateway: ${formData.pharmacy_gateway}`)
        const totalStates = new Set([
          ...(formData.states || []),
          ...(formData.states_controlled || []),
          ...(formData.states_non_sterile || []),
          ...(formData.states_sterile || [])
        ]).size
        if (totalStates > 0) details.push(`${totalStates} states`)
        if (formData.address?.city && formData.address?.state) {
          details.push(`${formData.address.city}, ${formData.address.state}`)
        }

        const description = operation === 'create'
          ? `Create: ${formData.pharmacy_name} (${details.join(', ')})`
          : `Update: ${formData.pharmacy_name} (${details.join(', ')})`

        addChange({
          resource_type: 'pharmacy',
          resource_id: pharmacy?._id || pharmacy?.pharmacy_ncpdpid || formData.pharmacy_ncpdpid,
          operation: operation,
          original_data: pharmacy || null,
          new_data: formData,
          description: description
        })

        onSave?.(formData)
        onClose()
        setHasChanges(false)
      }
    } catch (error) {
      console.error('Failed to save pharmacy to drafts:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose()
        setHasChanges(false)
      }
    } else {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {pharmacy ? `Edit Pharmacy - ${pharmacy.pharmacy_name}` : 'Create New Pharmacy'}
          </DialogTitle>
          <DialogDescription>
            Update pharmacy information, contact details, and configuration settings.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="licensing">Licensing</TabsTrigger>
            <TabsTrigger value="config">Configuration</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pharmacy_name">Pharmacy Name *</Label>
                <Input
                  id="pharmacy_name"
                  value={formData.pharmacy_name || ''}
                  onChange={(e) => updateFormData('pharmacy_name', e.target.value)}
                  className={validationErrors.pharmacy_name ? 'border-red-500' : ''}
                />
                {validationErrors.pharmacy_name && (
                  <p className="text-sm text-red-500">{validationErrors.pharmacy_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="pharmacy_ncpdpid">NCPDP ID *</Label>
                <Input
                  id="pharmacy_ncpdpid"
                  type="number"
                  value={formData.pharmacy_ncpdpid || ''}
                  onChange={(e) => updateFormData('pharmacy_ncpdpid', parseInt(e.target.value) || '')}
                  className={validationErrors.pharmacy_ncpdpid ? 'border-red-500' : ''}
                />
                {validationErrors.pharmacy_ncpdpid && (
                  <p className="text-sm text-red-500">{validationErrors.pharmacy_ncpdpid}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dosespot_pharmacy_id">DoseSpot ID</Label>
                <Input
                  id="dosespot_pharmacy_id"
                  type="number"
                  value={formData.dosespot_pharmacy_id || ''}
                  onChange={(e) => updateFormData('dosespot_pharmacy_id', parseInt(e.target.value) || '')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pharmacy_gateway">Gateway</Label>
                <Select
                  value={formData.pharmacy_gateway || '5Axis'}
                  onValueChange={(value) => updateFormData('pharmacy_gateway', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gateway" />
                  </SelectTrigger>
                  <SelectContent>
                    {PHARMACY_GATEWAYS.map((gateway) => (
                      <SelectItem key={gateway} value={gateway}>
                        {gateway}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Address</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    value={formData.address?.address || ''}
                    onChange={(e) => updateFormData('address.address', e.target.value)}
                    className={validationErrors['address.address'] ? 'border-red-500' : ''}
                  />
                  {validationErrors['address.address'] && (
                    <p className="text-sm text-red-500">{validationErrors['address.address']}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.address?.city || ''}
                    onChange={(e) => updateFormData('address.city', e.target.value)}
                    className={validationErrors['address.city'] ? 'border-red-500' : ''}
                  />
                  {validationErrors['address.city'] && (
                    <p className="text-sm text-red-500">{validationErrors['address.city']}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Select
                    value={formData.address?.state || ''}
                    onValueChange={(value) => updateFormData('address.state', value)}
                  >
                    <SelectTrigger className={validationErrors['address.state'] ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {validationErrors['address.state'] && (
                    <p className="text-sm text-red-500">{validationErrors['address.state']}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zipcode">Zipcode *</Label>
                  <Input
                    id="zipcode"
                    value={formData.address?.zipcode || ''}
                    onChange={(e) => updateFormData('address.zipcode', e.target.value)}
                    className={validationErrors['address.zipcode'] ? 'border-red-500' : ''}
                  />
                  {validationErrors['address.zipcode'] && (
                    <p className="text-sm text-red-500">{validationErrors['address.zipcode']}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.address?.country || 'USA'}
                    onChange={(e) => updateFormData('address.country', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contact Information Tab */}
          <TabsContent value="contact" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number *</Label>
              <Input
                id="phone_number"
                type="tel"
                value={formData.phone_number || ''}
                onChange={(e) => updateFormData('phone_number', e.target.value)}
                className={validationErrors.phone_number ? 'border-red-500' : ''}
              />
              {validationErrors.phone_number && (
                <p className="text-sm text-red-500">{validationErrors.phone_number}</p>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Contact Emails *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addContactEmail}
                >
                  Add Email
                </Button>
              </div>
              
              {formData.contact_emails?.map((email, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateContactEmail(index, e.target.value)}
                    placeholder="email@pharmacy.com"
                    className="flex-1"
                  />
                  {formData.contact_emails.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeContactEmail(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              
              {validationErrors.contact_emails && (
                <p className="text-sm text-red-500">{validationErrors.contact_emails}</p>
              )}
            </div>
          </TabsContent>

          {/* Licensing Tab */}
          <TabsContent value="licensing" className="space-y-6">
            {/* Controlled Substances */}
            <div className="space-y-3 p-4 border border-purple-200 dark:border-purple-800 rounded-lg bg-purple-50/50 dark:bg-purple-950/10">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Controlled Substances</Label>
                  <p className="text-xs text-muted-foreground">States licensed to dispense controlled substances (DEA)</p>
                </div>
                <Select onValueChange={(state) => addStateToField('states_controlled', state)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Add state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.filter(state => !formData.states_controlled?.includes(state)).map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {formData.states_controlled?.sort().map((state) => (
                  <Badge key={state} className="bg-purple-600 hover:bg-purple-700 cursor-pointer">
                    {state}
                    <button
                      type="button"
                      onClick={() => removeStateFromField('states_controlled', state)}
                      className="ml-1 hover:text-purple-200"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
                {(!formData.states_controlled || formData.states_controlled.length === 0) && (
                  <span className="text-sm text-muted-foreground italic">No states selected</span>
                )}
              </div>
            </div>

            {/* Sterile Compounds */}
            <div className="space-y-3 p-4 border border-green-200 dark:border-green-800 rounded-lg bg-green-50/50 dark:bg-green-950/10">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Sterile Compounds</Label>
                  <p className="text-xs text-muted-foreground">States licensed for sterile compounding (implies non-sterile)</p>
                </div>
                <Select onValueChange={(state) => addStateToField('states_sterile', state)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Add state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.filter(state => !formData.states_sterile?.includes(state)).map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {formData.states_sterile?.sort().map((state) => (
                  <Badge key={state} className="bg-green-600 hover:bg-green-700 cursor-pointer">
                    {state}
                    <button
                      type="button"
                      onClick={() => removeStateFromField('states_sterile', state)}
                      className="ml-1 hover:text-green-200"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
                {(!formData.states_sterile || formData.states_sterile.length === 0) && (
                  <span className="text-sm text-muted-foreground italic">No states selected</span>
                )}
              </div>
            </div>

            {/* Non-Sterile Compounds */}
            <div className="space-y-3 p-4 border border-orange-200 dark:border-orange-800 rounded-lg bg-orange-50/50 dark:bg-orange-950/10">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Non-Sterile Compounds</Label>
                  <p className="text-xs text-muted-foreground">States licensed for non-sterile compounding only</p>
                </div>
                <Select onValueChange={(state) => addStateToField('states_non_sterile', state)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Add state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.filter(state => !formData.states_non_sterile?.includes(state)).map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {formData.states_non_sterile?.sort().map((state) => (
                  <Badge key={state} className="bg-orange-600 hover:bg-orange-700 cursor-pointer">
                    {state}
                    <button
                      type="button"
                      onClick={() => removeStateFromField('states_non_sterile', state)}
                      className="ml-1 hover:text-orange-200"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
                {(!formData.states_non_sterile || formData.states_non_sterile.length === 0) && (
                  <span className="text-sm text-muted-foreground italic">No states selected</span>
                )}
              </div>
            </div>

            {/* Legacy States (Collapsible) */}
            {formData.states?.length > 0 && (
              <div className="space-y-3 p-4 border rounded-lg bg-gray-50 dark:bg-gray-900/20">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Legacy States</Label>
                    <p className="text-xs text-muted-foreground">Migrate these to the specific license types above</p>
                  </div>
                  <Select onValueChange={addState}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Add state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.filter(state => !formData.states?.includes(state)).map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {formData.states?.sort().map((state) => (
                    <Badge key={state} variant="outline" className="cursor-pointer hover:bg-red-50">
                      {state}
                      <button
                        type="button"
                        onClick={() => removeState(state)}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable_pharmacy"
                  checked={formData.pharmacy_config?.enable_pharmacy ?? true}
                  onCheckedChange={(checked) => updateFormData('pharmacy_config.enable_pharmacy', checked)}
                />
                <Label htmlFor="enable_pharmacy">Enable Pharmacy</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weekly_capacity">Weekly Order Capacity</Label>
                  <Input
                    id="weekly_capacity"
                    type="number"
                    min="0"
                    value={formData.pharmacy_config?.weekly_order_capacity || 100}
                    onChange={(e) => updateFormData('pharmacy_config.weekly_order_capacity', parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="current_orders">Current Week Orders</Label>
                  <Input
                    id="current_orders"
                    type="number"
                    min="0"
                    value={formData.pharmacy_config?.current_week_orders || 0}
                    onChange={(e) => updateFormData('pharmacy_config.current_week_orders', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="auto_accept_orders"
                    checked={formData.pharmacy_config?.auto_accept_orders ?? false}
                    onCheckedChange={(checked) => updateFormData('pharmacy_config.auto_accept_orders', checked)}
                  />
                  <Label htmlFor="auto_accept_orders">Auto-accept Orders</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="priority_processing"
                    checked={formData.pharmacy_config?.priority_processing ?? false}
                    onCheckedChange={(checked) => updateFormData('pharmacy_config.priority_processing', checked)}
                  />
                  <Label htmlFor="priority_processing">Priority Processing</Label>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !hasChanges}>
            {isLoading ? 'Saving to Drafts...' : 'Save to Drafts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
